import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, getAuthToken, removeAuthToken, updateAppBadge } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Users, ClipboardList, BarChart3, ArrowLeft, Shield, ShieldOff, Calendar, TrendingUp, AlertTriangle, Upload, Download, Briefcase, Gift, Send, Plus, Trash2, CheckCircle, XCircle, Key, UserCheck, Phone, Mail, Building2, History, ArrowRight, Search, FileText, Bell, PieChart, Activity, CheckCircle2, UserPlus, MessageSquare, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import * as XLSX from 'xlsx';

// IST (Indian Standard Time) formatting utility - Kolkata/Chennai timezone
// Using 12-hour format with AM/PM
const formatIST = (dateString, formatStr = 'MMM dd, h:mm a') => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const options = { timeZone: 'Asia/Kolkata' };
    const istDate = new Date(date.toLocaleString('en-US', options));
    return format(istDate, formatStr);
  } catch (e) {
    return dateString;
  }
};
const formatISTFull = (dateString) => formatIST(dateString, 'MMM dd, yyyy h:mm a');

const AdminPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [crmAnalytics, setCrmAnalytics] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [importing, setImporting] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [passwordLogs, setPasswordLogs] = useState([]);
  const [leadSpecialists, setLeadSpecialists] = useState([]);
  const [specialistContacts, setSpecialistContacts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedSpecialistEmail, setSelectedSpecialistEmail] = useState('all');
  const [statusHistoryDialog, setStatusHistoryDialog] = useState({ open: false, contact: null, history: [] });
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [allCandidateStatuses, setAllCandidateStatuses] = useState([]);
  const [candidateStatusSearch, setCandidateStatusSearch] = useState('');
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, email: '', name: '' });
  const [newPassword, setNewPassword] = useState('');
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [allPerformances, setAllPerformances] = useState([]);
  const [hrRoundSearch, setHrRoundSearch] = useState('');
  const [customNotification, setCustomNotification] = useState({ message: '', target_user: 'all' });
  const [availabilityStatus, setAvailabilityStatus] = useState('available');
  const [supportTickets, setSupportTickets] = useState([]);
  const [selectedSupportTicket, setSelectedSupportTicket] = useState(null);
  const [supportReplyMessage, setSupportReplyMessage] = useState('');
  
  // Skill Assessments State
  const [skillAssessments, setSkillAssessments] = useState([]);
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [newAssessment, setNewAssessment] = useState({
    skill_name: '',
    description: '',
    passing_score: 70,
    time_limit_minutes: 15,
    questions: []
  });
  const [newQuestion, setNewQuestion] = useState({
    skill: '',
    question: '',
    options: ['', '', '', ''],
    correct_answer: 0,
    difficulty: 'Medium'
  });
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    deadline: '',
    priority: 'Medium',
    assigned_to_email: ''
  });
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    description: '',
    bonus_amount: '',
    valid_until: '',
    priority: 'Medium',
    target_roles: ['all']
  });
  const [adminDeleteDialog, setAdminDeleteDialog] = useState({ open: false, userId: '', userName: '', password: '' });
  const [mainAdminActionDialog, setMainAdminActionDialog] = useState({ open: false, action: '', userId: '', userName: '', password: '', newRole: '' });
  const [referralSettings, setReferralSettings] = useState(null);
  const [referralEarnings, setReferralEarnings] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [editingReferralSettings, setEditingReferralSettings] = useState(false);
  const [referralSettingsForm, setReferralSettingsForm] = useState({
    reward_amount: 0,
    reward_currency: 'INR',
    hero_title: '',
    hero_description: '',
    terms_and_conditions: '',
    policy: '',
    min_withdrawal: 100,
    max_monthly_rewards: 10000,
    is_active: true,
    show_leaderboard: true
  });
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const contactFileInputRef = useRef(null);
  
  // Employer Management State
  const [employers, setEmployers] = useState([]);
  const [pendingEmployers, setPendingEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState(null);

  useEffect(() => {
    fetchAdminData();
  }, []);

  // Update app badge when notifications change
  useEffect(() => {
    const unreadCount = notifications.filter(n => !n.read).length;
    updateAppBadge(unreadCount);
  }, [notifications]);

  const fetchAdminData = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [userRes, usersRes, tasksRes, analyticsRes, crmAnalyticsRes, pendingRes, announcementsRes, referralsRes, passwordLogsRes, contactsRes, candidateStatusRes, notifRes, perfRes, supportRes] = await Promise.all([
        axios.get(`${API}/auth/me`, config),
        axios.get(`${API}/admin/users`, config),
        axios.get(`${API}/admin/tasks`, config),
        axios.get(`${API}/admin/analytics`, config),
        axios.get(`${API}/admin/crm-analytics`, config),
        axios.get(`${API}/admin/pending-users`, config),
        axios.get(`${API}/admin/referral-announcements`, config),
        axios.get(`${API}/admin/referrals`, config),
        axios.get(`${API}/admin/password-change-logs`, config),
        axios.get(`${API}/contacts`, config),
        axios.get(`${API}/admin/candidate-status`, config),
        axios.get(`${API}/notifications`, config),
        axios.get(`${API}/candidate-performance`, config),
        axios.get(`${API}/support-tickets`, config).catch(() => ({ data: [] }))
      ]);

      if (!userRes.data.is_admin) {
        toast.error('Access denied - Admin only');
        navigate('/');
        return;
      }

      setCurrentUser(userRes.data);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
      setTasks(tasksRes.data);
      setAnalytics(analyticsRes.data);
      setCrmAnalytics(crmAnalyticsRes.data);
      setAnnouncements(announcementsRes.data);
      setReferrals(referralsRes.data);
      setPasswordLogs(passwordLogsRes.data);
      setContacts(contactsRes.data);
      setAllCandidateStatuses(candidateStatusRes.data);
      setNotifications(notifRes.data);
      setAvailabilityStatus(userRes.data.availability_status || 'available');
      
      // Enrich performances with candidate info
      const enrichedPerf = perfRes.data.map(perf => {
        const candidate = candidateStatusRes.data.find(c => c.id === perf.candidate_status_id);
        return { ...perf, candidate_name: candidate?.candidate_name || 'Unknown', company_name: candidate?.company_name || '' };
      });
      setAllPerformances(enrichedPerf);
      setSupportTickets(supportRes.data);
      
      // Filter lead specialists
      const specialists = usersRes.data.filter(u => u.role === 'lead_specialist');
      setLeadSpecialists(specialists);
      
      // Fetch referral settings and earnings
      try {
        const [refSettingsRes, refEarningsRes] = await Promise.all([
          axios.get(`${API}/referral/settings`),
          axios.get(`${API}/admin/referral/earnings`, config)
        ]);
        setReferralSettings(refSettingsRes.data);
        setReferralSettingsForm({
          reward_amount: refSettingsRes.data.reward_amount || 500,
          reward_currency: refSettingsRes.data.reward_currency || 'INR',
          hero_title: refSettingsRes.data.hero_title || '',
          hero_description: refSettingsRes.data.hero_description || '',
          terms_and_conditions: refSettingsRes.data.terms_and_conditions || '',
          policy: refSettingsRes.data.policy || '',
          min_withdrawal: refSettingsRes.data.min_withdrawal || 100,
          max_monthly_rewards: refSettingsRes.data.max_monthly_rewards || 10000,
          is_active: refSettingsRes.data.is_active !== false,
          show_leaderboard: refSettingsRes.data.show_leaderboard !== false
        });
        setReferralEarnings(refEarningsRes.data.earnings || []);
        setReferralStats(refEarningsRes.data.stats || null);
      } catch (e) {
        console.error('Error fetching referral data:', e);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch Skill Assessments
  const fetchSkillAssessments = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/skill-assessments`, config);
      setSkillAssessments(res.data || []);
    } catch (e) {
      console.error('Error fetching assessments:', e);
    }
  };

  // Create new assessment
  const handleCreateAssessment = async () => {
    if (!newAssessment.skill_name || newAssessment.questions.length < 3) {
      toast.error('Please provide skill name and at least 3 questions');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/skill-assessments`, newAssessment, config);
      toast.success('Assessment created successfully!');
      setAssessmentDialogOpen(false);
      setNewAssessment({ skill_name: '', description: '', passing_score: 70, time_limit_minutes: 15, questions: [] });
      fetchSkillAssessments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assessment');
    }
  };

  // Add question to current assessment
  const addQuestionToAssessment = () => {
    if (!newQuestion.question || newQuestion.options.some(o => !o.trim())) {
      toast.error('Please fill in the question and all options');
      return;
    }
    setNewAssessment(prev => ({
      ...prev,
      questions: [...prev.questions, { ...newQuestion, id: Date.now().toString(), skill: prev.skill_name }]
    }));
    setNewQuestion({ skill: '', question: '', options: ['', '', '', ''], correct_answer: 0, difficulty: 'Medium' });
    toast.success('Question added!');
  };

  // Remove question from assessment
  const removeQuestionFromAssessment = (index) => {
    setNewAssessment(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  // Career Paths State
  const [careerPaths, setCareerPaths] = useState([]);
  const [careerPathDialogOpen, setCareerPathDialogOpen] = useState(false);
  const [newCareerPath, setNewCareerPath] = useState({
    domain: '',
    description: '',
    steps: [],
    skills_to_develop: [],
    certifications_recommended: []
  });
  const [newStep, setNewStep] = useState({
    role: '',
    level: 'Entry',
    typical_years: '0-2 years',
    skills_needed: [],
    avg_salary_range: '',
    description: ''
  });
  const [newSkill, setNewSkill] = useState('');
  const [newCert, setNewCert] = useState('');
  const [newStepSkill, setNewStepSkill] = useState('');

  // Fetch Career Paths
  const fetchCareerPaths = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/career-paths`, config);
      setCareerPaths(res.data || []);
    } catch (e) {
      console.error('Error fetching career paths:', e);
    }
  };

  // Create new career path
  const handleCreateCareerPath = async () => {
    if (!newCareerPath.domain || newCareerPath.steps.length < 2) {
      toast.error('Please provide domain name and at least 2 career stages');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/career-paths`, newCareerPath, config);
      toast.success('Career path created successfully!');
      setCareerPathDialogOpen(false);
      setNewCareerPath({ domain: '', description: '', steps: [], skills_to_develop: [], certifications_recommended: [] });
      fetchCareerPaths();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create career path');
    }
  };

  // Add step to career path
  const addStepToCareerPath = () => {
    if (!newStep.role || !newStep.avg_salary_range) {
      toast.error('Please fill in role title and salary range');
      return;
    }
    setNewCareerPath(prev => ({
      ...prev,
      steps: [...prev.steps, { ...newStep }]
    }));
    setNewStep({ role: '', level: 'Entry', typical_years: '0-2 years', skills_needed: [], avg_salary_range: '', description: '' });
    toast.success('Stage added!');
  };

  // Remove step from career path
  const removeStepFromCareerPath = (index) => {
    setNewCareerPath(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  // Toggle career path active status
  const toggleCareerPathStatus = async (pathId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/career-paths/${pathId}/toggle`, {}, config);
      toast.success('Status updated');
      fetchCareerPaths();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Delete career path
  const handleDeleteCareerPath = async (pathId) => {
    if (!confirm('Are you sure you want to delete this career path?')) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/admin/career-paths/${pathId}`, config);
      toast.success('Career path deleted');
      fetchCareerPaths();
    } catch (error) {
      toast.error('Failed to delete career path');
    }
  };

  // Fetch Employers
  const fetchEmployers = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [allRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/employers/all`, config),
        axios.get(`${API}/admin/pending-employers`, config)
      ]);
      setEmployers(Array.isArray(allRes.data) ? allRes.data : []);
      setPendingEmployers(Array.isArray(pendingRes.data) ? pendingRes.data : []);
    } catch (e) {
      console.error('Error fetching employers:', e);
    }
  };

  // Approve Employer
  const handleApproveEmployer = async (userId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/approve-employer/${userId}`, {}, config);
      toast.success('Employer approved successfully!');
      fetchEmployers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve employer');
    }
  };

  // Reject Employer
  const handleRejectEmployer = async (userId) => {
    if (!confirm('Are you sure you want to reject this employer?')) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/reject-employer/${userId}`, {}, config);
      toast.success('Employer rejected');
      fetchEmployers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject employer');
    }
  };

  const toggleAdminStatus = async (userId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/users/${userId}/admin`, {}, config);
      toast.success('Admin status updated');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update admin status');
    }
  };

  const updateAvailabilityStatus = async (status) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/users/availability?status=${status}`, {}, config);
      setAvailabilityStatus(status);
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const availabilityOptions = [
    { value: 'available', label: 'Available', color: 'from-emerald-500 to-green-500', icon: '🟢' },
    { value: 'offline', label: 'Offline', color: 'from-gray-500 to-slate-600', icon: '⚫' },
    { value: 'meeting', label: 'Meeting', color: 'from-blue-500 to-indigo-600', icon: '🔵' },
    { value: 'training', label: 'Training', color: 'from-amber-500 to-orange-500', icon: '🟡' },
    { value: 'system', label: 'System', color: 'from-red-500 to-rose-600', icon: '🔴' }
  ];

  const currentAvailability = availabilityOptions.find(a => a.value === availabilityStatus) || availabilityOptions[0];

  const handleApproveUser = async (userId, role) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/users/${userId}/approve?role=${role}`, {}, config);
      toast.success(`User approved as ${role}`);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/users/${userId}/reject`, {}, config);
      toast.success('User registration rejected');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };


  const handleDeactivateUser = async (userId) => {
    if (!confirm('Are you sure you want to revoke login access for this user?')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/users/${userId}/deactivate`, {}, config);
      toast.success('User login access revoked');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/users/${userId}/activate`, {}, config);
      toast.success('User login access restored');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const handleDeleteUser = async (userId, userName, isTargetAdmin = false) => {
    // If deleting an admin, require password confirmation
    if (isTargetAdmin) {
      setAdminDeleteDialog({ open: true, userId, userName, password: '' });
      return;
    }
    
    if (!confirm(`Are you sure you want to permanently delete user "${userName}"? This action cannot be undone.`)) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/admin/users/${userId}`, config);
      toast.success('User deleted successfully');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleDeleteAdminWithPassword = async () => {
    if (!adminDeleteDialog.password) {
      toast.error('Please enter your password');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Send delete request with password verification
      await axios.delete(`${API}/admin/users/${adminDeleteDialog.userId}`, {
        ...config,
        data: { password: adminDeleteDialog.password }
      });
      
      toast.success('Admin user deleted successfully');
      setAdminDeleteDialog({ open: false, userId: '', userName: '', password: '' });
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin. Check your password.');
    }
  };

  // Handle main admin (officiumconnect@gmail.com) actions with password verification
  const handleMainAdminAction = async () => {
    if (!mainAdminActionDialog.password) {
      toast.error('Please enter the main admin password');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Verify password for main admin actions
      const { action, userId, newRole, password } = mainAdminActionDialog;
      
      if (action === 'delete') {
        await axios.delete(`${API}/admin/users/${userId}`, {
          ...config,
          data: { password, main_admin_password: true }
        });
        toast.success('Main admin deleted successfully');
      } else if (action === 'change_role') {
        await axios.patch(`${API}/admin/users/${userId}/role?role=${newRole}`, 
          { password, main_admin_password: true }, 
          config
        );
        toast.success('Main admin role changed successfully');
      } else if (action === 'toggle_admin') {
        await axios.patch(`${API}/admin/users/${userId}/admin`, 
          { password, main_admin_password: true }, 
          config
        );
        toast.success('Main admin status changed successfully');
      } else if (action === 'deactivate') {
        await axios.patch(`${API}/admin/users/${userId}/deactivate`, 
          { password, main_admin_password: true }, 
          config
        );
        toast.success('Main admin deactivated successfully');
      }
      
      setMainAdminActionDialog({ open: false, action: '', userId: '', userName: '', password: '', newRole: '' });
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid password. Action denied.');
    }
  };

  const handleDeletePerformance = async (performanceId, candidateName) => {
    if (!confirm(`Are you sure you want to delete performance record for "${candidateName}"? This action cannot be undone.`)) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/candidate-performance/${performanceId}`, config);
      toast.success('Performance record deleted successfully');
      // Refresh performances
      const perfRes = await axios.get(`${API}/candidate-performance`, config);
      setAllPerformances(perfRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete performance record');
    }
  };

  const handleChangeUserRole = async (userId, newRole) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, config);
      toast.success(`User role changed to ${newRole}`);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
    }
  };


  const downloadTemplate = () => {
    const template = [
      { name: 'John Doe', email: 'john@example.com', phone: '+1234567890', password: 'password123' },
      { name: 'Jane Smith', email: 'jane@example.com', phone: '+0987654321', password: 'password123' }
    ];
    
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'users_import_template.xlsx');
    toast.success('Template downloaded');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/import-users`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Imported ${response.data.imported} users. Skipped ${response.data.skipped}.`);
      if (response.data.errors.length > 0) {
        toast.warning(`Some errors occurred: ${response.data.errors[0]}`);
      }
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleContactFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/import-contacts`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Imported ${response.data.imported} contacts. Skipped ${response.data.skipped}.`);
      if (response.data.errors.length > 0) {
        toast.warning(`Some errors occurred: ${response.data.errors[0]}`);
      }
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (contactFileInputRef.current) contactFileInputRef.current.value = '';
    }
  };

  const downloadContactTemplate = () => {
    const template = [
      { 
        name: 'John Doe', 
        phone: '+1234567890', 
        email: 'john@example.com', 
        company: 'ABC Corp', 
        years_of_experience: 5,
        skills: 'React, Node.js, Sales',
        highest_qualification: 'MBA',
        apply_to_company: 'Google',
        status: 'New Lead', 
        notes: 'Interested in product' 
      },
      { 
        name: 'Jane Smith', 
        phone: '+0987654321', 
        email: 'jane@example.com', 
        company: 'XYZ Inc', 
        years_of_experience: 3,
        skills: 'Python, Marketing',
        highest_qualification: 'B.Tech',
        apply_to_company: 'Microsoft',
        status: 'Contacted', 
        notes: 'Follow up next week' 
      }
    ];
    
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
    XLSX.writeFile(wb, 'contacts_import_template.xlsx');
    toast.success('Template downloaded');
  };

  const priorityColors = {
    Urgent: 'bg-red-500',
    High: 'bg-orange-500',
    Medium: 'bg-blue-500',
    Low: 'bg-gray-500',
    urgent: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-blue-500',
    low: 'bg-gray-500'
  };

  const priorityBorderColors = {
    Urgent: '#ef4444',
    High: '#f97316',
    Medium: '#3b82f6',
    Low: '#6b7280',
    urgent: '#ef4444',
    high: '#f97316',
    medium: '#3b82f6',
    low: '#6b7280'
  };

  const statusColors = {
    'Pending': 'bg-yellow-500',
    'In Progress': 'bg-blue-500',
    'Completed': 'bg-green-500',
    'pending': 'bg-yellow-500',
    'in_progress': 'bg-blue-500',
    'completed': 'bg-green-500'
  };

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.description.trim()) {
      toast.error('Please fill in title and description');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/admin/referral-announcements`, {
        ...newAnnouncement,
        valid_until: newAnnouncement.valid_until || null
      }, config);

      toast.success('Announcement created!');
      setAnnouncementDialogOpen(false);
      setNewAnnouncement({ title: '', description: '', bonus_amount: '', valid_until: '', priority: 'Medium', target_roles: ['all'] });
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!confirm('Delete this announcement?')) return;

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/admin/referral-announcements/${announcementId}`, config);
      toast.success('Announcement deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleUpdateReferralStatus = async (referralId, status) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/referrals/${referralId}?status=${status}`, {}, config);
      toast.success('Referral status updated');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const announcementPriorityColors = {
    Urgent: 'bg-red-500',
    High: 'bg-orange-500',
    Medium: 'bg-blue-500',
    Low: 'bg-gray-500'
  };

  const toggleTargetRole = (role) => {
    const current = newAnnouncement.target_roles;
    if (role === 'all') {
      setNewAnnouncement({ ...newAnnouncement, target_roles: ['all'] });
    } else {
      let updated = current.filter(r => r !== 'all');
      if (updated.includes(role)) {
        updated = updated.filter(r => r !== role);
      } else {
        updated.push(role);
      }
      if (updated.length === 0) updated = ['all'];
      setNewAnnouncement({ ...newAnnouncement, target_roles: updated });
    }
  };

  const referralStatusColors = {
    'Pending': 'bg-yellow-500',
    'Accepted': 'bg-blue-500',
    'Rejected': 'bg-red-500',
    'Hired': 'bg-green-500'
  };

  const candidateStatusColors = {
    'New Lead': 'bg-blue-500',
    'Contacted': 'bg-yellow-500',
    'Follow-up': 'bg-orange-500',
    'Interviewed': 'bg-purple-500',
    'Hired': 'bg-emerald-600',
    'Rejected': 'bg-red-500',
    'Converted': 'bg-green-500',
    'Lost': 'bg-gray-500',
    // For candidate status tracking
    'done': 'bg-green-500',
    'pass': 'bg-green-500',
    'received': 'bg-green-500',
    'sent': 'bg-green-500',
    'pending': 'bg-yellow-500',
    'not done': 'bg-yellow-500',
    'not received': 'bg-yellow-500',
    'failed': 'bg-red-500',
    'not-need': 'bg-gray-400',
    'not needed': 'bg-gray-400'
  };

  const handleUpdateCandidateStatus = async (contactId, newStatus) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/contacts/${contactId}`, { status: newStatus }, config);
      toast.success(`Status updated to ${newStatus}`);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewStatusHistory = async (contact) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/contacts/${contact.id}/status-history`, config);
      setStatusHistoryDialog({ open: true, contact, history: res.data });
    } catch (error) {
      toast.error('Failed to load status history');
    }
  };

  const getSpecialistStats = (specialistEmail) => {
    const assignedContacts = contacts.filter(c => c.assigned_to_specialist === specialistEmail);
    const specialistReferrals = referrals.filter(r => r.referred_by === specialistEmail);
    return {
      assigned: assignedContacts.length,
      hired: assignedContacts.filter(c => c.status === 'Hired').length,
      rejected: assignedContacts.filter(c => c.status === 'Rejected').length,
      referrals: specialistReferrals.length,
      successfulReferrals: specialistReferrals.filter(r => r.status === 'Hired').length
    };
  };

  const getFilteredContacts = () => {
    if (selectedSpecialistEmail === 'all') {
      return contacts.filter(c => c.assigned_to_specialist);
    }
    return contacts.filter(c => c.assigned_to_specialist === selectedSpecialistEmail);
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/admin/tasks/${taskId}`, config);
      toast.success('Task deleted successfully');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.deadline || !newTask.assigned_to_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/tasks`, newTask, config);
      toast.success('Task created successfully');
      setAddTaskDialogOpen(false);
      setNewTask({
        title: '',
        description: '',
        deadline: '',
        priority: 'Medium',
        assigned_to_email: ''
      });
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  // Sort tasks from newest to oldest
  const sortedTasks = [...tasks].sort((a, b) => {
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const handleResetUserPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(
        `${API}/admin/reset-user-password/${resetPasswordDialog.email}?new_password=${encodeURIComponent(newPassword)}`,
        {},
        config
      );
      toast.success(`Password reset for ${resetPasswordDialog.name}`);
      setResetPasswordDialog({ open: false, email: '', name: '' });
      setNewPassword('');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Support ticket functions
  const handleSupportReply = async () => {
    if (!supportReplyMessage.trim() || !selectedSupportTicket) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/support-tickets/${selectedSupportTicket.id}/reply`, { message: supportReplyMessage }, config);
      toast.success('Reply sent!');
      setSupportReplyMessage('');
      // Refresh tickets
      const ticketsRes = await axios.get(`${API}/support-tickets`, config);
      setSupportTickets(ticketsRes.data);
      const updatedTicket = ticketsRes.data.find(t => t.id === selectedSupportTicket.id);
      if (updatedTicket) setSelectedSupportTicket(updatedTicket);
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/support-tickets/${ticketId}/status?status=${status}`, {}, config);
      toast.success(`Ticket ${status.toLowerCase()}`);
      // Refresh tickets
      const ticketsRes = await axios.get(`${API}/support-tickets`, config);
      setSupportTickets(ticketsRes.data);
      if (selectedSupportTicket?.id === ticketId) {
        const updatedTicket = ticketsRes.data.find(t => t.id === ticketId);
        if (updatedTicket) setSelectedSupportTicket(updatedTicket);
      }
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-blue-100 text-blue-700';
      case 'In Progress': return 'bg-yellow-100 text-yellow-700';
      case 'Resolved': return 'bg-green-100 text-green-700';
      case 'Closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-gradient-to-r from-[#E8EDF4] to-[#F0F4F8] sticky top-0 z-50 shadow-sm border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-2 md:px-6 py-2 md:py-3 flex items-center justify-between">
          {/* Back + Logo + App Name */}
          <div className="flex items-center gap-1.5 md:gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-to-dashboard-button"
              className="h-7 w-7 md:h-9 md:w-9 text-[#1e3a5f] hover:bg-slate-200/50"
            >
              <ArrowLeft className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
            <img 
              src="/logo.png?v=15" 
              alt="Officium Connect" 
              className="h-8 md:h-10 rounded-lg"
              data-testid="admin-logo"
            />
            <span className="font-heading font-bold text-sm md:text-xl tracking-tight whitespace-nowrap text-[#1e3a5f]">
              Officium Connect
            </span>
          </div>

          {/* Availability + Notification + Role Badge */}
          <div className="flex items-center gap-1 md:gap-3">
            {/* Availability Status Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  className={`rounded-lg border-2 bg-gradient-to-r ${currentAvailability.color} text-white border-transparent hover:opacity-90 transition-all shadow-md h-7 md:h-9 px-2`}
                  data-testid="admin-availability-btn"
                >
                  <span className="mr-1">{currentAvailability.icon}</span>
                  <span className="hidden sm:inline text-xs">{currentAvailability.label}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="p-2">
                  <h3 className="font-heading font-semibold text-sm mb-2">Set Status</h3>
                  <div className="space-y-1">
                    {availabilityOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => updateAvailabilityStatus(option.value)}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-all text-sm ${
                          availabilityStatus === option.value ? 'bg-slate-100 font-medium' : ''
                        }`}
                      >
                        <span>{option.icon}</span>
                        <span>{option.label}</span>
                        {availabilityStatus === option.value && (
                          <CheckCircle2 className="h-4 w-4 ml-auto text-emerald-500" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-7 w-7 md:h-9 md:w-9 text-[#1e3a5f] hover:bg-slate-200/50" data-testid="admin-notifications-btn">
                  <Bell className="h-4 w-4 md:h-5 md:w-5" />
                  {notifications.filter(n => !n.read).length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-mono">
                      {notifications.filter(n => !n.read).length > 9 ? '9+' : notifications.filter(n => !n.read).length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <div className="p-3">
                  <h3 className="font-heading font-semibold text-sm mb-2">Notifications</h3>
                  {notifications.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">No notifications</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {notifications.slice(0, 5).map((notif) => (
                        <div key={notif.id} className={`p-2 rounded-sm text-xs ${notif.read ? 'opacity-60' : 'bg-blue-50'}`}>
                          <p>{notif.message}</p>
                          <p className="text-muted-foreground font-mono text-[10px] mt-1">{formatIST(notif.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <Badge 
              className="font-mono text-[10px] md:text-xs bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white px-1.5 md:px-2 shadow-md"
              data-testid="admin-role-badge"
            >
              <Shield className="h-3 w-3 mr-0.5 md:mr-1" />
              <span className="hidden sm:inline">Admin</span>
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        <Tabs defaultValue="referral-settings" className="space-y-4 md:space-y-6">
          {/* Admin Panel - Only 4 tabs: Referrals, Assessments, Careers, Employers */}
          <TabsList className="flex justify-center gap-3 bg-white/50 backdrop-blur-sm p-4 rounded-2xl shadow-lg border border-white/20">
            <TabsTrigger 
              value="referral-settings" 
              data-testid="referral-settings-tab" 
              className="group flex flex-col items-center gap-1.5 p-3 md:p-4 text-xs md:text-sm bg-white/80 border-0 shadow-md hover:shadow-lg data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-xl rounded-xl transition-all min-w-[90px] md:min-w-[110px]"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow group-data-[state=active]:from-white/20 group-data-[state=active]:to-white/10">
                <Gift className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <span className="font-medium">Referrals</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="assessments" 
              data-testid="assessments-tab" 
              className="group flex flex-col items-center gap-1.5 p-3 md:p-4 text-xs md:text-sm bg-white/80 border-0 shadow-md hover:shadow-lg data-[state=active]:bg-gradient-to-br data-[state=active]:from-purple-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-xl rounded-xl transition-all min-w-[90px] md:min-w-[110px]"
              onClick={fetchSkillAssessments}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-purple-400 to-violet-500 flex items-center justify-center shadow group-data-[state=active]:from-white/20 group-data-[state=active]:to-white/10">
                <ClipboardList className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <span className="font-medium">Assessments</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="career-paths" 
              data-testid="career-paths-tab" 
              className="group flex flex-col items-center gap-1.5 p-3 md:p-4 text-xs md:text-sm bg-white/80 border-0 shadow-md hover:shadow-lg data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-500 data-[state=active]:to-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-xl rounded-xl transition-all min-w-[90px] md:min-w-[110px]"
              onClick={fetchCareerPaths}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center shadow group-data-[state=active]:from-white/20 group-data-[state=active]:to-white/10">
                <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <span className="font-medium">Careers</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="employers" 
              data-testid="employers-tab" 
              className="group flex flex-col items-center gap-1.5 p-3 md:p-4 text-xs md:text-sm bg-white/80 border-0 shadow-md hover:shadow-lg data-[state=active]:bg-gradient-to-br data-[state=active]:from-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-xl rounded-xl transition-all min-w-[90px] md:min-w-[110px]"
              onClick={fetchEmployers}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow group-data-[state=active]:from-white/20 group-data-[state=active]:to-white/10">
                <Building2 className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <span className="font-medium">Employers</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Referral Settings Tab */}
          <TabsContent value="referral-settings" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-semibold">Refer & Earn Management</h3>
                <p className="text-sm text-muted-foreground">Configure referral rewards and track earnings</p>
              </div>
              <Button 
                onClick={() => window.open('/refer', '_blank')}
                variant="outline"
                className="gap-2"
              >
                <Gift className="h-4 w-4" />
                View Public Page
              </Button>
            </div>

            {/* Stats Cards */}
            {referralStats && (
              <div className="grid gap-4 md:grid-cols-4 mb-6">
                <Card className="rounded-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Referrals</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">{referralStats.total}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Pending Rewards</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-600">{referralStats.pending}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Eligible for Reward</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{referralStats.eligible}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Credited</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">₹{referralStats.total_credited_amount?.toLocaleString()}</div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Settings Form */}
              <Card className="rounded-sm">
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Referral Program Settings</span>
                    <Badge className={referralSettingsForm.is_active ? 'bg-green-500' : 'bg-red-500'}>
                      {referralSettingsForm.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Banner Image Upload */}
                  <div>
                    <Label className="flex items-center justify-between mb-2">
                      <span>Banner Image</span>
                      {referralSettings?.banner_image_url && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700 h-6 px-2"
                          onClick={async () => {
                            try {
                              const token = getAuthToken();
                              await axios.delete(`${API}/admin/referral/banner`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Banner removed');
                              fetchAdminData();
                            } catch (e) {
                              toast.error('Failed to remove banner');
                            }
                          }}
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Remove
                        </Button>
                      )}
                    </Label>
                    {referralSettings?.banner_image_url ? (
                      <div className="border rounded-lg p-2 bg-gray-50">
                        <img 
                          src={`${API.replace('/api', '')}${referralSettings.banner_image_url}`} 
                          alt="Banner" 
                          className="w-full h-24 object-cover rounded"
                        />
                      </div>
                    ) : (
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <input 
                          type="file" 
                          accept="image/*" 
                          ref={bannerInputRef}
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setBannerUploading(true);
                            try {
                              const token = getAuthToken();
                              const formData = new FormData();
                              formData.append('file', file);
                              await axios.post(`${API}/admin/referral/banner-upload`, formData, {
                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                              });
                              toast.success('Banner uploaded');
                              fetchAdminData();
                            } catch (err) {
                              toast.error('Failed to upload banner');
                            } finally {
                              setBannerUploading(false);
                            }
                          }}
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={bannerUploading}
                          onClick={() => bannerInputRef.current?.click()}
                        >
                          {bannerUploading ? 'Uploading...' : 'Upload Banner Image'}
                        </Button>
                        <p className="text-xs text-muted-foreground mt-1">Recommended: 1200x400px</p>
                      </div>
                    )}
                  </div>

                  {/* Reward Amount - Set to 0 to hide */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Reward Amount (₹) <span className="text-xs text-gray-400">(0 = hidden)</span></Label>
                      <Input 
                        type="number"
                        value={referralSettingsForm.reward_amount}
                        onChange={(e) => setReferralSettingsForm({...referralSettingsForm, reward_amount: parseFloat(e.target.value) || 0})}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label>Min Withdrawal (₹)</Label>
                      <Input 
                        type="number"
                        value={referralSettingsForm.min_withdrawal}
                        onChange={(e) => setReferralSettingsForm({...referralSettingsForm, min_withdrawal: parseFloat(e.target.value)})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Hero Title</Label>
                    <Input 
                      value={referralSettingsForm.hero_title}
                      onChange={(e) => setReferralSettingsForm({...referralSettingsForm, hero_title: e.target.value})}
                      placeholder="Refer & Earn Rewards"
                    />
                  </div>
                  <div>
                    <Label>Hero Description</Label>
                    <Textarea 
                      value={referralSettingsForm.hero_description}
                      onChange={(e) => setReferralSettingsForm({...referralSettingsForm, hero_description: e.target.value})}
                      placeholder="Invite friends to join..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="flex items-center justify-between">
                      <span>Terms & Conditions</span>
                      {referralSettingsForm.terms_and_conditions && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700 h-6 px-2"
                          onClick={() => setReferralSettingsForm({...referralSettingsForm, terms_and_conditions: ''})}
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Clear
                        </Button>
                      )}
                    </Label>
                    <Textarea 
                      value={referralSettingsForm.terms_and_conditions}
                      onChange={(e) => setReferralSettingsForm({...referralSettingsForm, terms_and_conditions: e.target.value})}
                      placeholder="Leave empty to hide this section on public page..."
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Leave empty to hide on public page</p>
                  </div>
                  <div>
                    <Label className="flex items-center justify-between">
                      <span>Referral Policy</span>
                      {referralSettingsForm.policy && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700 h-6 px-2"
                          onClick={() => setReferralSettingsForm({...referralSettingsForm, policy: ''})}
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Clear
                        </Button>
                      )}
                    </Label>
                    <Textarea 
                      value={referralSettingsForm.policy}
                      onChange={(e) => setReferralSettingsForm({...referralSettingsForm, policy: e.target.value})}
                      placeholder="Add eligibility rules, fraud prevention policy, etc. Leave empty to hide..."
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Leave empty to hide on public page</p>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <Label className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={referralSettingsForm.is_active}
                        onChange={(e) => setReferralSettingsForm({...referralSettingsForm, is_active: e.target.checked})}
                        className="rounded"
                      />
                      Program Active
                    </Label>
                    <Label className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={referralSettingsForm.show_leaderboard}
                        onChange={(e) => setReferralSettingsForm({...referralSettingsForm, show_leaderboard: e.target.checked})}
                        className="rounded"
                      />
                      Show Leaderboard
                    </Label>
                  </div>
                  <Button 
                    onClick={async () => {
                      try {
                        const token = getAuthToken();
                        await axios.put(`${API}/admin/referral/settings`, referralSettingsForm, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('Settings updated successfully');
                        fetchAdminData();
                      } catch (e) {
                        toast.error('Failed to update settings');
                      }
                    }}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  >
                    Save Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Referral Earnings List */}
              <Card className="rounded-sm">
                <CardHeader>
                  <CardTitle className="text-base">Recent Referral Earnings</CardTitle>
                  <CardDescription>Track and manage referral rewards</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    {referralEarnings.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Gift className="h-12 w-12 mx-auto mb-2 opacity-20" />
                        <p>No referrals yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {referralEarnings.map((earning) => (
                          <div key={earning.id} className="border rounded-lg p-3 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-sm">{earning.referred_name}</p>
                                <p className="text-xs text-muted-foreground">{earning.referred_email}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Referred by: <span className="font-medium">{earning.referrer_name}</span>
                                </p>
                                {earning.job_title && (
                                  <p className="text-xs text-blue-600 mt-1">{earning.job_title}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <Badge className={
                                  earning.status === 'Telephone_Cleared' ? 'bg-green-100 text-green-800' :
                                  earning.status === 'Hired' ? 'bg-emerald-100 text-emerald-800' :
                                  earning.status === 'Registered' ? 'bg-blue-100 text-blue-800' :
                                  earning.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }>
                                  {earning.status?.replace('_', ' ')}
                                </Badge>
                                <p className="text-xs mt-1">
                                  <Badge className={
                                    earning.reward_status === 'Credited' ? 'bg-purple-100 text-purple-800' :
                                    earning.reward_status === 'Eligible' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                  }>
                                    {earning.reward_status === 'Credited' ? `₹${earning.reward_amount} Credited` : earning.reward_status}
                                  </Badge>
                                </p>
                              </div>
                            </div>
                            {earning.reward_status === 'Eligible' && (
                              <div className="mt-2 pt-2 border-t flex gap-2">
                                <Button 
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-xs"
                                  onClick={async () => {
                                    try {
                                      const token = getAuthToken();
                                      await axios.post(`${API}/admin/referral/earning/${earning.id}/credit`, {}, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success('Reward credited!');
                                      fetchAdminData();
                                    } catch (e) {
                                      toast.error('Failed to credit reward');
                                    }
                                  }}
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Credit ₹{earning.reward_amount}
                                </Button>
                              </div>
                            )}
                            {earning.status === 'Registered' && (
                              <div className="mt-2 pt-2 border-t">
                                <Select
                                  defaultValue={earning.status}
                                  onValueChange={async (value) => {
                                    try {
                                      const token = getAuthToken();
                                      await axios.patch(`${API}/admin/referral/earning/${earning.id}/status?status=${value}`, {}, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success('Status updated');
                                      fetchAdminData();
                                    } catch (e) {
                                      toast.error('Failed to update status');
                                    }
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Update Status" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Registered">Registered</SelectItem>
                                    <SelectItem value="Telephone_Cleared">Telephone Cleared</SelectItem>
                                    <SelectItem value="Hired">Hired</SelectItem>
                                    <SelectItem value="Rejected">Rejected</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Skill Assessments Tab */}
          <TabsContent value="assessments" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h3 className="font-heading text-lg font-semibold">Skill Assessments</h3>
                <p className="text-sm text-muted-foreground">Create and manage skill assessments for candidates</p>
              </div>
              <Dialog open={assessmentDialogOpen} onOpenChange={setAssessmentDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 w-full sm:w-auto">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Assessment
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Skill Assessment</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {/* Assessment Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Skill Name *</Label>
                        <Input
                          value={newAssessment.skill_name}
                          onChange={(e) => setNewAssessment({...newAssessment, skill_name: e.target.value})}
                          placeholder="e.g., Python Programming"
                        />
                      </div>
                      <div>
                        <Label>Passing Score (%)</Label>
                        <Input
                          type="number"
                          value={newAssessment.passing_score}
                          onChange={(e) => setNewAssessment({...newAssessment, passing_score: parseInt(e.target.value)})}
                          min="50"
                          max="100"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newAssessment.description}
                        onChange={(e) => setNewAssessment({...newAssessment, description: e.target.value})}
                        placeholder="Brief description of the assessment"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Time Limit (minutes)</Label>
                      <Input
                        type="number"
                        value={newAssessment.time_limit_minutes}
                        onChange={(e) => setNewAssessment({...newAssessment, time_limit_minutes: parseInt(e.target.value)})}
                        min="5"
                        max="60"
                      />
                    </div>

                    {/* Questions List */}
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">Questions ({newAssessment.questions.length})</h4>
                      {newAssessment.questions.length > 0 && (
                        <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                          {newAssessment.questions.map((q, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                              <div className="flex-1">
                                <p className="text-sm font-medium truncate">{idx + 1}. {q.question}</p>
                                <p className="text-xs text-gray-500">Correct: Option {q.correct_answer + 1} • {q.difficulty}</p>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => removeQuestionFromAssessment(idx)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Question Form */}
                      <div className="p-4 border rounded-lg bg-purple-50/50">
                        <h5 className="font-medium text-sm mb-3">Add New Question</h5>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Question *</Label>
                            <Textarea
                              value={newQuestion.question}
                              onChange={(e) => setNewQuestion({...newQuestion, question: e.target.value})}
                              placeholder="Enter your question"
                              rows={2}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {newQuestion.options.map((opt, idx) => (
                              <div key={idx}>
                                <Label className="text-xs">Option {idx + 1} {idx === newQuestion.correct_answer && '✓'}</Label>
                                <div className="flex gap-1">
                                  <Input
                                    value={opt}
                                    onChange={(e) => {
                                      const opts = [...newQuestion.options];
                                      opts[idx] = e.target.value;
                                      setNewQuestion({...newQuestion, options: opts});
                                    }}
                                    placeholder={`Option ${idx + 1}`}
                                    className={idx === newQuestion.correct_answer ? 'border-green-500' : ''}
                                  />
                                  <Button 
                                    variant={idx === newQuestion.correct_answer ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setNewQuestion({...newQuestion, correct_answer: idx})}
                                    className={idx === newQuestion.correct_answer ? 'bg-green-500 hover:bg-green-600' : ''}
                                  >
                                    ✓
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Select value={newQuestion.difficulty} onValueChange={(v) => setNewQuestion({...newQuestion, difficulty: v})}>
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Easy">Easy</SelectItem>
                                <SelectItem value="Medium">Medium</SelectItem>
                                <SelectItem value="Hard">Hard</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button onClick={addQuestionToAssessment} variant="outline" className="flex-1">
                              <Plus className="h-4 w-4 mr-1" />
                              Add Question
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button variant="outline" onClick={() => setAssessmentDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateAssessment} 
                        className="bg-gradient-to-r from-purple-500 to-violet-600"
                        disabled={!newAssessment.skill_name || newAssessment.questions.length < 3}
                      >
                        Create Assessment ({newAssessment.questions.length} questions)
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Existing Assessments */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {skillAssessments.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center">
                    <ClipboardList className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Assessments Yet</h3>
                    <p className="text-gray-500 mb-4">Create skill assessments for candidates to verify their skills</p>
                    <Button onClick={() => setAssessmentDialogOpen(true)} className="bg-gradient-to-r from-purple-500 to-violet-600">
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Assessment
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                skillAssessments.map((assessment) => (
                  <Card key={assessment.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{assessment.skill_name}</CardTitle>
                          <CardDescription>{assessment.description || 'No description'}</CardDescription>
                        </div>
                        <Badge className={assessment.is_active ? 'bg-green-500' : 'bg-gray-400'}>
                          {assessment.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Questions:</span>
                          <span className="font-medium">{assessment.questions?.length || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Passing Score:</span>
                          <span className="font-medium">{assessment.passing_score}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Time Limit:</span>
                          <span className="font-medium">{assessment.time_limit_minutes} mins</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Created:</span>
                          <span className="font-medium">{formatIST(assessment.created_at, 'MMM dd, yyyy')}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Career Paths Tab */}
          <TabsContent value="career-paths" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-semibold">Career Paths</h3>
                <p className="text-sm text-muted-foreground">Create and manage career progression paths for candidates</p>
              </div>
              <Dialog open={careerPathDialogOpen} onOpenChange={setCareerPathDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Career Path
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Career Path</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {/* Career Path Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Domain/Industry *</Label>
                        <Input
                          value={newCareerPath.domain}
                          onChange={(e) => setNewCareerPath({...newCareerPath, domain: e.target.value})}
                          placeholder="e.g., Software Engineering, Data Science, Product Management"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Description</Label>
                        <Textarea
                          value={newCareerPath.description}
                          onChange={(e) => setNewCareerPath({...newCareerPath, description: e.target.value})}
                          placeholder="Brief description of this career path"
                          rows={2}
                        />
                      </div>
                    </div>

                    {/* Skills to Develop */}
                    <div>
                      <Label>Key Skills to Develop</Label>
                      <div className="flex gap-2 mb-2">
                        <Input
                          value={newSkill}
                          onChange={(e) => setNewSkill(e.target.value)}
                          placeholder="Add a skill"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newSkill.trim()) {
                              setNewCareerPath(prev => ({...prev, skills_to_develop: [...prev.skills_to_develop, newSkill.trim()]}));
                              setNewSkill('');
                            }
                          }}
                        />
                        <Button variant="outline" onClick={() => {
                          if (newSkill.trim()) {
                            setNewCareerPath(prev => ({...prev, skills_to_develop: [...prev.skills_to_develop, newSkill.trim()]}));
                            setNewSkill('');
                          }
                        }}>Add</Button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {newCareerPath.skills_to_develop.map((skill, idx) => (
                          <Badge key={idx} variant="secondary" className="cursor-pointer" onClick={() => {
                            setNewCareerPath(prev => ({...prev, skills_to_develop: prev.skills_to_develop.filter((_, i) => i !== idx)}));
                          }}>{skill} ×</Badge>
                        ))}
                      </div>
                    </div>

                    {/* Recommended Certifications */}
                    <div>
                      <Label>Recommended Certifications</Label>
                      <div className="flex gap-2 mb-2">
                        <Input
                          value={newCert}
                          onChange={(e) => setNewCert(e.target.value)}
                          placeholder="Add a certification"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newCert.trim()) {
                              setNewCareerPath(prev => ({...prev, certifications_recommended: [...(prev.certifications_recommended || []), newCert.trim()]}));
                              setNewCert('');
                            }
                          }}
                        />
                        <Button variant="outline" onClick={() => {
                          if (newCert.trim()) {
                            setNewCareerPath(prev => ({...prev, certifications_recommended: [...(prev.certifications_recommended || []), newCert.trim()]}));
                            setNewCert('');
                          }
                        }}>Add</Button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(newCareerPath.certifications_recommended || []).map((cert, idx) => (
                          <Badge key={idx} variant="outline" className="cursor-pointer" onClick={() => {
                            setNewCareerPath(prev => ({...prev, certifications_recommended: prev.certifications_recommended.filter((_, i) => i !== idx)}));
                          }}>{cert} ×</Badge>
                        ))}
                      </div>
                    </div>

                    {/* Career Stages */}
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">Career Stages ({newCareerPath.steps.length})</h4>
                      {newCareerPath.steps.length > 0 && (
                        <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                          {newCareerPath.steps.map((step, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">{idx + 1}</Badge>
                                  <p className="font-medium">{step.role}</p>
                                  <Badge>{step.level}</Badge>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{step.typical_years} • {step.avg_salary_range}</p>
                                {step.skills_needed.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {step.skills_needed.slice(0, 3).map((s, i) => (
                                      <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                                    ))}
                                    {step.skills_needed.length > 3 && <Badge variant="secondary" className="text-xs">+{step.skills_needed.length - 3}</Badge>}
                                  </div>
                                )}
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => removeStepFromCareerPath(idx)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Stage Form */}
                      <div className="p-4 border rounded-lg bg-blue-50/50">
                        <h5 className="font-medium text-sm mb-3">Add Career Stage</h5>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Role Title *</Label>
                            <Input
                              value={newStep.role}
                              onChange={(e) => setNewStep({...newStep, role: e.target.value})}
                              placeholder="e.g., Junior Developer"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Level</Label>
                            <Select value={newStep.level} onValueChange={(v) => setNewStep({...newStep, level: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Entry">Entry</SelectItem>
                                <SelectItem value="Junior">Junior</SelectItem>
                                <SelectItem value="Mid">Mid</SelectItem>
                                <SelectItem value="Senior">Senior</SelectItem>
                                <SelectItem value="Lead">Lead</SelectItem>
                                <SelectItem value="Manager">Manager</SelectItem>
                                <SelectItem value="Director">Director</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Typical Years</Label>
                            <Select value={newStep.typical_years} onValueChange={(v) => setNewStep({...newStep, typical_years: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0-2 years">0-2 years</SelectItem>
                                <SelectItem value="2-4 years">2-4 years</SelectItem>
                                <SelectItem value="4-6 years">4-6 years</SelectItem>
                                <SelectItem value="6-8 years">6-8 years</SelectItem>
                                <SelectItem value="8-10 years">8-10 years</SelectItem>
                                <SelectItem value="10+ years">10+ years</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Salary Range *</Label>
                            <Input
                              value={newStep.avg_salary_range}
                              onChange={(e) => setNewStep({...newStep, avg_salary_range: e.target.value})}
                              placeholder="e.g., ₹6L - ₹12L"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Skills Needed</Label>
                            <div className="flex gap-2">
                              <Input
                                value={newStepSkill}
                                onChange={(e) => setNewStepSkill(e.target.value)}
                                placeholder="Add skill"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && newStepSkill.trim()) {
                                    setNewStep(prev => ({...prev, skills_needed: [...prev.skills_needed, newStepSkill.trim()]}));
                                    setNewStepSkill('');
                                  }
                                }}
                              />
                              <Button variant="outline" size="sm" onClick={() => {
                                if (newStepSkill.trim()) {
                                  setNewStep(prev => ({...prev, skills_needed: [...prev.skills_needed, newStepSkill.trim()]}));
                                  setNewStepSkill('');
                                }
                              }}>+</Button>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {newStep.skills_needed.map((skill, idx) => (
                                <Badge key={idx} variant="secondary" className="cursor-pointer text-xs" onClick={() => {
                                  setNewStep(prev => ({...prev, skills_needed: prev.skills_needed.filter((_, i) => i !== idx)}));
                                }}>{skill} ×</Badge>
                              ))}
                            </div>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Description</Label>
                            <Textarea
                              value={newStep.description}
                              onChange={(e) => setNewStep({...newStep, description: e.target.value})}
                              placeholder="Brief description of this role"
                              rows={2}
                            />
                          </div>
                        </div>
                        <Button onClick={addStepToCareerPath} variant="outline" className="w-full mt-3">
                          <Plus className="h-4 w-4 mr-1" />
                          Add Stage
                        </Button>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button variant="outline" onClick={() => setCareerPathDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateCareerPath} 
                        className="bg-gradient-to-r from-blue-500 to-cyan-600"
                        disabled={!newCareerPath.domain || newCareerPath.steps.length < 2}
                      >
                        Create Career Path ({newCareerPath.steps.length} stages)
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Existing Career Paths */}
            <div className="grid gap-4 md:grid-cols-2">
              {careerPaths.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center">
                    <TrendingUp className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Career Paths Yet</h3>
                    <p className="text-gray-500 mb-4">Create career progression paths to guide candidates</p>
                    <Button onClick={() => setCareerPathDialogOpen(true)} className="bg-gradient-to-r from-blue-500 to-cyan-600">
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Career Path
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                careerPaths.map((path) => (
                  <Card key={path.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-blue-500" />
                            {path.domain}
                          </CardTitle>
                          <CardDescription className="line-clamp-2">{path.description || 'No description'}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            className={`cursor-pointer ${path.is_active ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500'}`}
                            onClick={() => toggleCareerPathStatus(path.id)}
                          >
                            {path.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCareerPath(path.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Career Stages Preview */}
                      <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-2">
                        {path.steps?.map((step, idx) => (
                          <React.Fragment key={idx}>
                            <div className="flex-shrink-0 px-2 py-1 bg-blue-100 rounded text-xs font-medium text-blue-700">
                              {step.role}
                            </div>
                            {idx < path.steps.length - 1 && (
                              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      
                      {/* Skills Preview */}
                      {path.skills_to_develop?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {path.skills_to_develop.slice(0, 4).map((skill, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">{skill}</Badge>
                          ))}
                          {path.skills_to_develop.length > 4 && (
                            <Badge variant="outline" className="text-xs">+{path.skills_to_develop.length - 4}</Badge>
                          )}
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500">
                        {path.steps?.length || 0} stages • Created {formatIST(path.created_at, 'MMM dd, yyyy')}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Employers Management Tab */}
          <TabsContent value="employers" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-semibold">Employer Management</h3>
                <p className="text-sm text-muted-foreground">View, approve, and manage employer accounts</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-amber-600 border-amber-600">
                  {pendingEmployers.length} Pending
                </Badge>
                <Badge variant="outline" className="text-green-600 border-green-600">
                  {employers.filter(e => e.status === 'approved').length} Active
                </Badge>
              </div>
            </div>

            {/* Pending Employers */}
            {pendingEmployers.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Pending Approvals ({pendingEmployers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingEmployers.map((employer) => (
                      <div key={employer.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-amber-200">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-semibold text-lg">
                            {employer.company_name?.charAt(0)?.toUpperCase() || 'E'}
                          </div>
                          <div>
                            <p className="font-medium">{employer.company_name || employer.name}</p>
                            <p className="text-sm text-gray-500">{employer.email}</p>
                            <p className="text-xs text-gray-400">Applied: {formatIST(employer.created_at, 'MMM dd, yyyy')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleApproveEmployer(employer.id)}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleRejectEmployer(employer.id)}
                            className="text-red-500 border-red-500 hover:bg-red-50"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Employers */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-indigo-500" />
                  All Employers ({employers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {employers.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Employers Yet</h3>
                    <p className="text-gray-500">Employers will appear here once they register</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Company</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Contact</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Jobs Posted</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Registered</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employers.map((employer) => (
                          <tr key={employer.id} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                                  {employer.company_name?.charAt(0)?.toUpperCase() || 'E'}
                                </div>
                                <div>
                                  <p className="font-medium">{employer.company_name || 'N/A'}</p>
                                  <p className="text-xs text-gray-500">{employer.industry || 'Not specified'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-sm">{employer.name}</p>
                              <p className="text-xs text-gray-500">{employer.email}</p>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={
                                employer.status === 'approved' ? 'bg-green-500' :
                                employer.status === 'pending' ? 'bg-amber-500' :
                                'bg-red-500'
                              }>
                                {employer.status || 'pending'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-medium">{employer.jobs_count || 0}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-gray-500">
                                {formatIST(employer.created_at, 'MMM dd, yyyy')}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {employer.status === 'pending' && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleApproveEmployer(employer.id)}
                                      className="text-green-600 border-green-600 hover:bg-green-50"
                                    >
                                      Approve
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleRejectEmployer(employer.id)}
                                      className="text-red-500 border-red-500 hover:bg-red-50"
                                    >
                                      Reject
                                    </Button>
                                  </>
                                )}
                                {employer.status === 'approved' && (
                                  <Badge variant="outline" className="text-green-600">Active</Badge>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPanel;